import { useEffect, useMemo, useState } from "react";
import { EditorProvider, useCurrentEditor, Mark } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Image from "@tiptap/extension-image";
import Table from "@tiptap/extension-table";
import TableRow from "@tiptap/extension-table-row";
import TableCell from "@tiptap/extension-table-cell";
import TableHeader from "@tiptap/extension-table-header";
import { Color } from "@tiptap/extension-color";
import TextStyle from "@tiptap/extension-text-style";
import TextAlign from "@tiptap/extension-text-align";
import Paragraph from "@tiptap/extension-paragraph";
import { errorToast, successToast } from "../../helpers";
import { Uploadpdf } from "../../utils/mediaUpload";
import {
  Box,
  Button,
  IconButton,
  MenuItem,
  Select,
  SelectChangeEvent,
  Tooltip,
  Typography,
} from "@mui/material";
import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatItalicIcon from "@mui/icons-material/FormatItalic";
import StrikethroughSIcon from "@mui/icons-material/StrikethroughS";
import FormatAlignLeftIcon from "@mui/icons-material/FormatAlignLeft";
import FormatAlignCenterIcon from "@mui/icons-material/FormatAlignCenter";
import FormatAlignRightIcon from "@mui/icons-material/FormatAlignRight";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import FormatListNumberedIcon from "@mui/icons-material/FormatListNumbered";
import UndoIcon from "@mui/icons-material/Undo";
import RedoIcon from "@mui/icons-material/Redo";
import {
  useMinPaymentUsersMutation,
  useSendOfferLetterMutation,
} from "../../service/offerLetter";
import { generateEncryptedKeyBody } from "../../utils/crypto";
import { CommonBody, OfferLetterUsers } from "../../types/General";
import { Document, Page } from "react-pdf";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import Loader from "../../constants/Loader";

const CustomImage = Image.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      style: {
        default: null,
        parseHTML: (element) => element.getAttribute("style") || null,
        renderHTML: (attributes) => {
          if (!attributes.style) return {};
          return { style: attributes.style };
        },
      },
    };
  },
});

const FontSize = Mark.create({
  name: "fontSize",

  addAttributes() {
    return {
      size: {
        default: null,
        parseHTML: (element) => element.style.fontSize || null,
        renderHTML: (attributes) => {
          if (!attributes.size) return {};
          return { style: `font-size: ${attributes.size}` };
        },
      },
    };
  },

  parseHTML() {
    return [
      {
        style: "font-size",
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return ["span", HTMLAttributes, 0];
  },
});

const CustomParagraph = Paragraph.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      style: {
        default: null,
        parseHTML: (element) => element.getAttribute("style") || null,
        renderHTML: (attributes) => {
          if (!attributes.style) return {};
          return {
            style: attributes.style,
          };
        },
      },
    };
  },
});

const extensions = [
  Color.configure({ types: [TextStyle.name] }),
  StarterKit,
  TextStyle,
  FontSize,
  TableRow,
  TableCell,
  TableHeader,
  CustomParagraph,
  Table.extend({
    addAttributes() {
      return {
        style: {
          parseHTML: (element) => element.style?.cssText,
        },
      };
    },
    renderHTML(props) {
      return ["table", props.HTMLAttributes, 0];
    },
  }),
  TextAlign.configure({
    types: ["heading", "paragraph"],
  }),
  CustomImage,
];

enum ACTIVE_SCREEN {
  EDITOR_SCREEN = 1,
  REVIEW_SCREEN = 2,
}

const DummyOfferLetter = () => {
  const [pdfUrl, setPdfUrl] = useState("");
  const [pdfId, setPdfId] = useState("");
  const [pageNumber, setPageNumber] = useState(1);
  const [numPages, setNumPages] = useState(null);
  const [userData, setUserData] = useState<OfferLetterUsers[]>([]);
  const [activeScreen, setActiveScreen] = useState(ACTIVE_SCREEN.EDITOR_SCREEN);
  const [selectedUser, setSelectedUser] = useState<null | {
    id: number;
    full_name: string;
    email: string;
    visa_type: string;
    apk_id: string;
  }>(null);

  const onDocumentLoadSuccess = ({ numPages }: any) => {
    setNumPages(numPages);
  };
  const goToPrevPage = () =>
    setPageNumber(pageNumber - 1 <= 1 ? 1 : pageNumber - 1);

  const goToNextPage = () => {
    if (numPages !== null) {
      setPageNumber(pageNumber + 1 >= numPages ? numPages : pageNumber + 1);
    }
  };

  const generateContent = (user: { full_name: any } | null) => {
    return `
  <table style="width: 100%; background: linear-gradient(to right, #1a59cb 70%, #dcdcdc 70%); padding: 20px;">
    <tr>
      <td >
      <p style="font-size: 12px; color: #fff; margin: 0;">
        Devon Rd, Hempstead, NY | yourinfo@emailaddress.com <br />
        WWW.TEMPLATE.NET | 222 555 7777
        </p>
      </td>
      <td>
        <img src="/static/images/logo.png" alt="Logo" style="width: 120px; height: 65px; object-fit: contain;" />
      </td>
    </tr>
  </table>
  <p style="font-size: 24px; font-weight: bold; padding-top: 20px; color:black;">
    Job Offer Letter
  </p>
  <p style="color:black;">
    April 4, 2050<br><br>
    <strong>Efrain Abshire</strong><br>
    Shreveport, LA 71101<br>
    efrain@you.mail<br><br>
    Dear ${user?.full_name || "[User Name]"},<br><br>
    We were impressed with your skills and experiences, and we believe you would substantially contribute to our team and our company's future.<br><br>
    Your starting date will be April 10, 2050, and you will report directly to your Supervisor, <strong>Ms. Sophie Fadel</strong>. 
    The starting salary for your position is $650.00, payable bi-weekly, along with any other company benefits or bonuses.<br><br>
    This offer is contingent upon your agreement to the terms of employment. If you should have any questions, please feel free to reach out. We're looking forward to having you on our team.<br><br>
    Sincerely,<br><br>
    <strong>[Your Name]</strong><br>
    Human Resource Manager<br>
    <strong>[Your Company Email]</strong>
  </p>
  `;
  };

  const [editorContent, setEditorContent] = useState(generateContent(null));
  const [loading, setLoading] = useState(false);

  const [userList] = useMinPaymentUsersMutation();
  const [sendLetter] = useSendOfferLetterMutation();

  const getUsersList = async () => {
    try {
      const body = {
        start: 1,
        length: 500,
        search: "",
      };

      const encryptedBody = generateEncryptedKeyBody(body) as CommonBody;
      const response = await userList(encryptedBody).unwrap();

      if (response?.status === 200) {
        setUserData(response?.data?.response_object || []);
      }
    } catch (error: any) {
      errorToast(error?.data?.message || "");
    }
  };

  const sendOfferLetter = async () => {
    try {
      setLoading(true);

      const body = {
        letter: [pdfId],
      };

      const encryptedBody = generateEncryptedKeyBody(body) as CommonBody;
      const response = await sendLetter({
        body: encryptedBody,
        id: selectedUser?.id,
      }).unwrap();
      setLoading(false);
      if (response?.status === 200) {
        successToast("Offer letter sent successfully!");
        window.location.reload();
      }
    } catch (error: any) {
      setLoading(false);
      errorToast(error?.data?.message || "");
    }
  };

  const convertToPdf = () => {
    if (editorContent?.length < 100) {
      errorToast("Minimum 100 characters are required.");
      return;
    }
    if (!selectedUser) {
      errorToast("Please select a user to send offer letter");
      return;
    }

    if (editorContent) {
      const opt = {
        margin: 0.5,
        filename: "offer_letter.pdf",
        image: { type: "jpeg", quality: 0.98 },
        html2canvas: {
          scale: 2,
        },
        jsPDF: { unit: "in", format: "letter", orientation: "portrait" },
      };

      import("html2pdf.js").then((html2pdf) => {
        html2pdf
          .default()
          .from(editorContent)
          .set(opt)
          .toPdf()
          .get("pdf")
          .then(async function (pdf: { output: (arg0: string) => any }) {
            const blob = pdf.output("blob");
            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");

            link.href = url;
            const response = await Uploadpdf(blob);

            if (response?.data?.[0]?.media_url) {
              setPdfUrl(response?.data?.[0]?.media_url);
              setPdfId(response?.data?.[0]?.id);
              setActiveScreen(activeScreen + 1);
              // setPdfGenerated(!pdfGenerated)

              // const reader = new FileReader();
              // reader.readAsDataURL(blob);
              // reader.onloadend = function () {
              //   const base64data = reader.result;
              // };
            } else {
              errorToast("Something went wrong");
            }
          });
      });
    }
  };

  const memoizedFile = useMemo(() => ({ url: pdfUrl }), [pdfUrl]);

  const MenuBar = () => {
    const { editor } = useCurrentEditor();

    if (!editor) {
      return null;
    }
    const isActive = (name: any, options = {}) =>
      editor.isActive(name, options);

    const handleSelectedUsers = (event: SelectChangeEvent) => {
      const selectedUserObject = JSON.parse(event.target.value);
      setSelectedUser(selectedUserObject);
      const newContent = generateContent(selectedUserObject);
      setEditorContent(newContent);

      if (editor) {
        editor.commands.setContent(newContent); // <- this updates the editor's content
      }
    };

    return (
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: "8px",
          marginBottom: 20,
        }}
      >
        <Tooltip title="Bold">
          <span>
            <IconButton
              onClick={() => editor.chain().focus().toggleBold().run()}
              disabled={!editor.can().chain().focus().toggleBold().run()}
              color={isActive("bold") ? "primary" : "default"}
            >
              <FormatBoldIcon />
            </IconButton>
          </span>
        </Tooltip>

        <Tooltip title="Italic">
          <span>
            <IconButton
              onClick={() => editor.chain().focus().toggleItalic().run()}
              disabled={!editor.can().chain().focus().toggleItalic().run()}
              color={isActive("italic") ? "primary" : "default"}
            >
              <FormatItalicIcon />
            </IconButton>
          </span>
        </Tooltip>

        <Tooltip title="Strikethrough">
          <span>
            <IconButton
              onClick={() => editor.chain().focus().toggleStrike().run()}
              disabled={!editor.can().chain().focus().toggleStrike().run()}
              color={isActive("strike") ? "primary" : "default"}
            >
              <StrikethroughSIcon />
            </IconButton>
          </span>
        </Tooltip>

        <Tooltip title="Align Left">
          <IconButton
            onClick={() => editor.chain().focus().setTextAlign("left").run()}
            color={isActive({ textAlign: "left" }) ? "primary" : "default"}
          >
            <FormatAlignLeftIcon />
          </IconButton>
        </Tooltip>

        <Tooltip title="Align Center">
          <IconButton
            onClick={() => editor.chain().focus().setTextAlign("center").run()}
            color={isActive({ textAlign: "center" }) ? "primary" : "default"}
          >
            <FormatAlignCenterIcon />
          </IconButton>
        </Tooltip>

        <Tooltip title="Align Right">
          <IconButton
            onClick={() => editor.chain().focus().setTextAlign("right").run()}
            color={isActive({ textAlign: "right" }) ? "primary" : "default"}
          >
            <FormatAlignRightIcon />
          </IconButton>
        </Tooltip>

        <Tooltip title="Bullet List">
          <IconButton
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            color={isActive("bulletList") ? "primary" : "default"}
          >
            <FormatListBulletedIcon />
          </IconButton>
        </Tooltip>

        <Tooltip title="Numbered List">
          <IconButton
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            color={isActive("orderedList") ? "primary" : "default"}
          >
            <FormatListNumberedIcon />
          </IconButton>
        </Tooltip>

        <Tooltip title="Undo">
          <span>
            <IconButton
              onClick={() => editor.chain().focus().undo().run()}
              disabled={!editor.can().chain().focus().undo().run()}
            >
              <UndoIcon />
            </IconButton>
          </span>
        </Tooltip>

        <Tooltip title="Redo">
          <span>
            <IconButton
              onClick={() => editor.chain().focus().redo().run()}
              disabled={!editor.can().chain().focus().redo().run()}
            >
              <RedoIcon />
            </IconButton>
          </span>
        </Tooltip>

        <Select
          className="fontSize_dropdown"
          defaultValue=""
          onChange={(e: { target: { value: any } }) => {
            editor
              .chain()
              .focus()
              .setMark("fontSize", { size: e.target.value })
              .run();
          }}
          displayEmpty
          size="small"
          style={{ minWidth: "120px" }}
        >
          <MenuItem value="" disabled>
            Font Size
          </MenuItem>
          <MenuItem value="12px">12px</MenuItem>
          <MenuItem value="14px">14px</MenuItem>
          <MenuItem value="16px">16px</MenuItem>
          <MenuItem value="18px">18px</MenuItem>
          <MenuItem value="24px">24px</MenuItem>
          <MenuItem value="32px">32px</MenuItem>
        </Select>
        <Select
          className="fontSize_dropdown"
          labelId="religion"
          id="religion"
          value={selectedUser ? JSON.stringify(selectedUser) : "default"}
          onChange={handleSelectedUsers}
          style={{ minWidth: "120px" }}
        >
          <MenuItem disabled value="default">
            Select User
          </MenuItem>

          {userData?.length
            ? userData?.map((item) => {
                return (
                  <MenuItem value={JSON.stringify(item)} key={item?.id}>
                    <div>
                      <p
                        style={{
                          color: "black",
                          paddingBottom: "2px",
                          fontWeight: 400,
                        }}
                      >
                        {item?.full_name || "-"} ({item?.visa_type || ""})
                      </p>
                      <p style={{ color: "black", fontWeight: 400 }}>
                        {item?.apk_id || ""}
                      </p>
                    </div>
                  </MenuItem>
                );
              })
            : null}
        </Select>
      </div>
    );
  };
  useEffect(() => {
    getUsersList();
  }, []);

  return (
    <>
      <Loader isLoad={loading} />
      <div
        id="pdf-content"
        style={{ display: "none" }}
        dangerouslySetInnerHTML={{ __html: editorContent }}
      />
      {activeScreen === ACTIVE_SCREEN.EDITOR_SCREEN ? (
        <div>
          <EditorProvider
            slotBefore={<MenuBar />}
            extensions={extensions}
            content={editorContent}
            onUpdate={({ editor }) => {
              const html = editor.getHTML();
              setEditorContent(html);
            }}
          />
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              marginTop: "20px",
            }}
          >
            <Button variant="contained" onClick={convertToPdf}>
              Next
            </Button>
          </div>
        </div>
      ) : activeScreen === ACTIVE_SCREEN.REVIEW_SCREEN ? (
        <>
          <Typography variant="h5">Review the Offer Letter</Typography>

          <p>
            {" "}
            Please confirm the information in the offer letter and ensure it is
            correct.
          </p>
          <p className="preview_link">
            <a href={pdfUrl} target="_blank" rel="noopener noreferrer">
              Can't see the preview? Click here to download
            </a>
          </p>

          <>
            <Box className="docs_div">
              <a href={pdfUrl} target="_blank" rel="noopener noreferrer">
                <Document
                  file={memoizedFile}
                  onLoadSuccess={onDocumentLoadSuccess}
                >
                  <Page
                    pageNumber={pageNumber}
                    renderMode="canvas"
                    width={800}
                  />
                </Document>
              </a>
              <div className="pdf_page_btn">
                <p>
                  Page {pageNumber} of {numPages}
                </p>
                <IconButton onClick={goToPrevPage}>
                  <ArrowBackIosNewIcon />
                </IconButton>
                <IconButton onClick={goToNextPage}>
                  <ArrowForwardIosIcon />
                </IconButton>
              </div>
            </Box>
          </>
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: "20px",
              marginTop: "20px",
            }}
          >
            <Button
              variant="contained"
              onClick={() => setActiveScreen(activeScreen - 1)}
            >
              Back
            </Button>
            <Button variant="contained" onClick={sendOfferLetter}>
              Send
            </Button>
          </div>
        </>
      ) : null}
    </>
  );
};

export default DummyOfferLetter;
